import { useState } from 'react';
import { Bell, MapPin, Shield, AlertTriangle, Settings } from 'lucide-react';
import { requestNotificationPermission } from '../../lib/pushSession';
import type { Resource } from '../../types';

interface MdtPermissionsGateProps {
  resource: Resource;
  onComplete: (opts: { notifications: boolean; location: boolean }) => void;
}

type Step = 'notifications' | 'location' | 'confirm';

export default function MdtPermissionsGate({ resource, onComplete }: MdtPermissionsGateProps) {
  const [step, setStep] = useState<Step>('notifications');
  const [notificationsOk, setNotificationsOk] = useState(false);
  const [locationOk, setLocationOk] = useState(false);
  const [limitedMode, setLimitedMode] = useState(false);

  const requestLocation = (): Promise<boolean> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

  const handleAllowNotifications = async () => {
    const result = await requestNotificationPermission();
    const ok = result === 'granted';
    setNotificationsOk(ok);
    if (!ok) setLimitedMode(true);
    setStep('location');
  };

  const handleAllowLocation = async () => {
    const ok = await requestLocation();
    setLocationOk(ok);
    if (!ok) setLimitedMode(true);
    setStep('confirm');
  };

  const handleStartShift = () => {
    onComplete({ notifications: notificationsOk, location: locationOk });
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex flex-col items-center justify-center p-6">
      <div className="fixed inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: 'rgba(102,148,255,0.2)', border: '1px solid rgba(102,148,255,0.15)' }}
          >
            <Shield className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">Start your shift</h1>
          <p className="text-sm text-white/50">
            Unit <span className="text-blue-400 font-semibold">{resource.name}</span> — permissions apply only while logged in
          </p>
        </div>

        {limitedMode && (
          <div className="flex gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200/90">
              Some permissions were denied. You can continue in limited mode or open device Settings to enable them.
            </div>
          </div>
        )}

        {step === 'notifications' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Bell className="w-8 h-8 text-blue-400" />
              <div>
                <h2 className="text-base font-bold text-white">Dispatch notifications</h2>
                <p className="text-xs text-white/50 mt-1">
                  Receive incident and message alerts while on duty. Notifications stop when you log out.
                </p>
              </div>
            </div>
            <button
              onClick={handleAllowNotifications}
              className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold text-sm"
            >
              Allow notifications
            </button>
            <button
              onClick={() => { setLimitedMode(true); setStep('location'); }}
              className="w-full py-2 text-white/40 text-xs"
            >
              Skip for now
            </button>
          </div>
        )}

        {step === 'location' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-emerald-400" />
              <div>
                <h2 className="text-base font-bold text-white">Unit location</h2>
                <p className="text-xs text-white/50 mt-1">
                  Share your location with Command while logged in. Tracking stops on logout.
                </p>
              </div>
            </div>
            <button
              onClick={handleAllowLocation}
              className="w-full py-3 rounded-xl bg-emerald-500 text-white font-bold text-sm"
            >
              Allow location
            </button>
            <button
              onClick={() => { setLimitedMode(true); setStep('confirm'); }}
              className="w-full py-2 text-white/40 text-xs"
            >
              Skip for now
            </button>
          </div>
        )}

        {step === 'confirm' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <h2 className="text-base font-bold text-white text-center">Ready to go on duty</h2>
            <ul className="text-xs text-white/60 space-y-2">
              <li className={notificationsOk ? 'text-emerald-400' : 'text-amber-400'}>
                Notifications: {notificationsOk ? 'Enabled' : 'Limited — in-app alerts only'}
              </li>
              <li className={locationOk ? 'text-emerald-400' : 'text-amber-400'}>
                Location: {locationOk ? 'Enabled' : 'Limited — no map tracking'}
              </li>
              <li className="text-white/40">All services stop when you tap Logout</li>
            </ul>
            {limitedMode && (
              <button
                onClick={() => {
                  try {
                    window.open('app-settings:', '_blank');
                  } catch { /* ignore */ }
                }}
                className="w-full py-2 flex items-center justify-center gap-2 text-xs text-white/50"
              >
                <Settings className="w-3 h-3" /> Open Settings
              </button>
            )}
            <button
              onClick={handleStartShift}
              className="w-full py-4 rounded-xl bg-blue-500 text-white font-extrabold text-sm"
            >
              Start shift
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
